import { useTranslation } from "react-i18next";
import { Dropdown, DropdownDivider, DropdownItem, Icon, Input } from "semantic-ui-react";

import { useDropdownSelector } from "components/TableFilters/TableDropdownSelector/hooks";
import "components/TableFilters/TableDropdownSelector/TableDropdownSelector.scss";

import type { DropdownItemProps } from "components/Dropdown";
import type { ChangeEvent, ReactNode } from "react";
import type { DropdownProps } from "semantic-ui-react";

export interface DropdownTableSelectorProps extends DropdownProps {
  selected: string[];
  trigger: ReactNode;
  options: DropdownItemProps<string | number>[];
  multiple?: boolean;
  withSearch?: boolean;
  showFilterIcon?: boolean;
  customMenuStyle?: { [key: string]: string | number };
  handleOnSelect: (value: string[]) => void;
  direction?: "left" | "right";
}

export const TableDropdownSelector = ({
  selected,
  trigger,
  options,
  multiple = false,
  withSearch = false,
  showFilterIcon = true,
  customMenuStyle,
  handleOnSelect,
  direction = "left"
}: DropdownTableSelectorProps) => {
  const { t } = useTranslation();

  const { dropdownOptions, handleOnClick, handleOpen, handleClose, setSearchTerm, showDropdown } = useDropdownSelector({
    handleOnSelect,
    options,
    selected,
    multiple,
    withSearch,
    trigger
  });

  const isSelected = (value: string) => selected.includes(value);

  return (
    <div className="TableDropdownSelector">
      <Dropdown
        value={multiple ? [] : ""}
        multiple={multiple}
        open={showDropdown}
        onOpen={handleOpen}
        onClose={handleClose}
        closeOnBlur={true}
        selectOnBlur={false}
        icon={null}
        direction={direction}
        closeOnChange={!multiple}
        scrolling
        className="custom-dropdown"
        trigger={
          <div className="carret-header-wrapper">
            {trigger}
            {showFilterIcon && <Icon className={`${selected.includes("all") ? "filter" : "filters"} table-dropdown-filters-icon`} size="small" />}
          </div>
        }
        inline
      >
        <Dropdown.Menu style={customMenuStyle} onClick={(e: Event) => e.stopPropagation()} onKeyDown={(e: Event) => e.stopPropagation()}>
          {withSearch && (
            <>
              <Input
                icon={<Icon className="magnifying glass" />}
                iconPosition="left"
                className="search"
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                placeholder={`${t("v8_search")}...` || "Search..."}
              />
              <DropdownDivider />
            </>
          )}

          {dropdownOptions.length ? (
            dropdownOptions.map(option => {
              const selected = isSelected(option.value.toString());

              return (
                <DropdownItem
                  key={option.value}
                  value={option.value.toString()}
                  selected={selected}
                  onClick={(e, data) => handleOnClick(e, data.value?.toString() || "")}
                  className={`item-width ${option.icon && selected ? "icon-item" : ""}`}
                >
                  <div>
                    {option.icon && <Icon className={option.icon} style={{ color: option.iconColor }} />}
                    <span className={`text ellipsis ellipsis-wo-nr ${selected && (option.icon ? "icon-selected-text-color" : "selected-text-color")}`}>
                      {option.text}
                    </span>
                  </div>
                </DropdownItem>
              );
            })
          ) : (
            <DropdownItem>
              <span className="selected-text-color"> {t("v8_no_results_found", "No results found")} </span>
            </DropdownItem>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};
